import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ClientWorkspaceTemplateResponse,
  WorkspaceTemplateResponse,
  WorkspaceTemplateItemResponse
} from '../interfaces/ClientWorkspaceTemplateResponse';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceTemplateService {
  // Private subjects
  private workspaceTemplateDataSubject = new BehaviorSubject<ClientWorkspaceTemplateResponse | null>(null);
  private workspaceTemplatesSubject = new BehaviorSubject<WorkspaceTemplateResponse[]>([]);
  private selectedTemplateSubject = new BehaviorSubject<WorkspaceTemplateResponse | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  workspaceTemplateData$: Observable<ClientWorkspaceTemplateResponse | null> = 
    this.workspaceTemplateDataSubject.asObservable();
  workspaceTemplates$: Observable<WorkspaceTemplateResponse[]> = 
    this.workspaceTemplatesSubject.asObservable();
  selectedTemplate$: Observable<WorkspaceTemplateResponse | null> = 
    this.selectedTemplateSubject.asObservable();
  isLoading$: Observable<boolean> = 
    this.isLoadingSubject.asObservable();

  // Setters
  setWorkspaceTemplateData(data: ClientWorkspaceTemplateResponse | null): void {
    this.workspaceTemplateDataSubject.next(data);
    if (data?.workspaceTemplates) {
      this.workspaceTemplatesSubject.next(data.workspaceTemplates);
    }
  }

  setWorkspaceTemplates(templates: WorkspaceTemplateResponse[]): void {
    this.workspaceTemplatesSubject.next(templates);
  }

  setSelectedTemplate(template: WorkspaceTemplateResponse | null): void {
    this.selectedTemplateSubject.next(template);
  }

  setLoading(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  // Getters (for immediate synchronous access)
  getWorkspaceTemplateData(): ClientWorkspaceTemplateResponse | null {
    return this.workspaceTemplateDataSubject.value;
  }

  getWorkspaceTemplates(): WorkspaceTemplateResponse[] {
    return this.workspaceTemplatesSubject.value;
  }

  getSelectedTemplate(): WorkspaceTemplateResponse | null {
    return this.selectedTemplateSubject.value;
  }

  isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  // Utility methods
  findTemplateByGuid(guid: string): WorkspaceTemplateResponse | undefined {
    return this.workspaceTemplatesSubject.value.find(
      template => template.workspaceTemplateGuid === guid
    );
  }

  updateTemplate(updatedTemplate: WorkspaceTemplateResponse): void {
    const templates = this.workspaceTemplatesSubject.value;
    const index = templates.findIndex(
      t => t.workspaceTemplateGuid === updatedTemplate.workspaceTemplateGuid
    );
    
    if (index !== -1) {
      templates[index] = updatedTemplate;
      this.workspaceTemplatesSubject.next([...templates]);
    }
  }

  addTemplate(newTemplate: WorkspaceTemplateResponse): void {
    const templates = [...this.workspaceTemplatesSubject.value, newTemplate];
    this.workspaceTemplatesSubject.next(templates);
  }

  removeTemplate(templateGuid: string): void {
    const templates = this.workspaceTemplatesSubject.value.filter(
      t => t.workspaceTemplateGuid !== templateGuid
    );
    this.workspaceTemplatesSubject.next(templates);
  }

  // Clear all data
  clearData(): void {
    this.workspaceTemplateDataSubject.next(null);
    this.workspaceTemplatesSubject.next([]);
    this.selectedTemplateSubject.next(null);
    this.isLoadingSubject.next(false);
  }
}